import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import BreadCrumbComp from "../../components/breadCrumb";
import SimpleTable from "../../components/tables/simpleTable";
import { icons } from "../../constants";
import FormTextField from "../../components/textField/formTextField";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import LaddaButton from "../../components/buttons/laddaButton";
import ToastNotification from "../../components/toastNotification";
import VerificationModal from "../../components/modals/verificationModal";
import SimpleSelect from "../../components/selects/simpleSelect";
import ModalComponent from "../../components/modals";
import { isInterestedPartiesModuleAdmin } from "../../utils/interestedPartiesPermissions";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  callApiAsync,
  createUpdateStakeholdersExternalRequirement,
  deleteStakeholdersExternalRequirement,
  getStakeholdersExternalRequirements,
} from "../../services/apiService";
import { stakeholdersTabType } from "../../constants/enums";
import { YesNoOptionsSelect } from "../../constants/selectData";

const validationSchema = yup.object().shape({
  requirement: yup.string().required("La necesidad es obligatoria"),
  expectation: yup.string().required("La expectativa es obligatoria"),
  accountability: yup
    .string()
    .required("La rendición de cuentas es obligatoria"),
  monitoring_sources: yup.string().required("El monitoreo es obligatorio"),
  legal_requirement: yup.string().required("Selecciona un requisito legal"),
  organization_requirement: yup
    .string()
    .required("Selecciona un requisito de la organización"),
  customer_requirement: yup
    .string()
    .required("Selecciona un requisito del cliente"),
  frequency: yup.string().required("La frecuencia es obligatoria"),
});

const columnsInterestedPartiesExternalBusinessCustomer = [
  { field: "requirement", header: "Necesidad" },
  { field: "expectation", header: "Expectativa" },
  { field: "accountability", header: "Rendición de Cuentas" },
  { field: "monitoring_sources", header: "Fuentes de Monitoreo" },
  {
    field: "legal_requirement",
    header: "Requisito Legal",
    columnType: "custom",
    render: (rowData) => (
      <p className="table-default-column">
        {rowData.legal_requirement === 1 ? "Si" : "No"}
      </p>
    ),
  },
  {
    field: "organization_requirement",
    header: "Requisito de la organización",
    columnType: "custom",
    render: (rowData) => (
      <p className="table-default-column">
        {rowData.organization_requirement === 1 ? "Si" : "No"}
      </p>
    ),
  },
  {
    field: "customer_requirement",
    header: "Requisito del cliente",
    columnType: "custom",
    render: (rowData) => (
      <p className="table-default-column">
        {rowData.customer_requirement === 1 ? "Si" : "No"}
      </p>
    ),
  },
  { field: "frequency", header: "Frecuencia" },
  { field: "actions", header: "", columnType: "actionButtons" },
];

const DetailBusinessCustomers = () => {
  const {
    control,
    reset,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const [open, setOpen] = useState(false);
  const [openDeleteVerification, setOpenDeleteVerification] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [viewOnly, setViewOnly] = useState(false);
  const renderData = useSelector(
    (state) => state.breadCrumbData.breadCrumbData
  );
  const location = useLocation();
  const rowData = location.state?.rowData;
  const [openVerification, setOpenVerification] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "success",
    summary: "",
    detail: "",
  });

  const getTableData = async () => {
    await callApiAsync(
      () => getStakeholdersExternalRequirements(rowData?.id),
      (data) => {
        setTableData(data);
      }
    );
  };

  const userData = useSelector((state) => state.userData.userData);
  const isEditMode = Boolean(selectedRow?.id);

  const canEditModuleStakeholders = isInterestedPartiesModuleAdmin(
    userData?.roleList
  );

  const openModal = (row = null, isViewOnly = false) => {
    if (row) {
      reset({
        requirement: row.requirement ?? "",
        expectation: row.expectation ?? "",
        accountability: row.accountability ?? "",
        monitoring_sources: row.monitoring_sources ?? "",
        legal_requirement:
          row.legal_requirement === 1
            ? true
            : row.legal_requirement === 0
            ? false
            : undefined,
        organization_requirement:
          row.organization_requirement === 1
            ? true
            : row.organization_requirement === 0
            ? false
            : undefined,
        customer_requirement:
          row.customer_requirement === 1
            ? true
            : row.customer_requirement === 0
            ? false
            : undefined,
        frequency: row.frequency ?? "",
      });
      setSelectedRow(row);
    } else {
      reset({
        requirement: "",
        expectation: "",
        accountability: "",
        monitoring_sources: "",
        legal_requirement: undefined,
        organization_requirement: undefined,
        customer_requirement: undefined,
        frequency: "",
      });
      setSelectedRow(null);
    }
    setViewOnly(isViewOnly);
    setOpen(true);
  };

  const closeModal = () => {
    reset({
      requirement: "",
      expectation: "",
      accountability: "",
      monitoring_sources: "",
      legal_requirement: "",
      organization_requirement: "",
      customer_requirement: "",
      frequency: "",
    });
    setValue("legal_requirement", "");
    setValue("organization_requirement", null);
    setValue("customer_requirement", null);
    setSelectedEvent(null);
    setSelectedRow(null);
    setViewOnly(false);
    setOpen(false);
  };

  const handleSaveBannerConfirmed = async () => {
    if (!canEditModuleStakeholders) return;
    const formValues = getValues();

    const dataToSend = {
      ...formValues,
      id: selectedRow?.id ?? 0,
      type: stakeholdersTabType.external,
      user_editor_id: userData?.id,
      stakeholders_external_id: rowData.id,
    };

    await callApiAsync(
      () => createUpdateStakeholdersExternalRequirement(dataToSend),
      () => {
        setToastConfig({
          severity: "success",
          summary: isEditMode ? "Editado" : "Creado",
          detail: `Se ${isEditMode ? "editó" : "creó"} la necesidad con éxito.`,
        });
        setShowToast(true);
        getTableData();
      }
    );
    handleCloseVerification();
    closeModal();
  };

  const deleteInterestedPartiesExternalDetail = async () => {
    if (!canEditModuleStakeholders) return;
    await callApiAsync(
      () =>
        deleteStakeholdersExternalRequirement({
          user_editor_id: userData.id,
          type: stakeholdersTabType.byProcess,
          id: selectedRow.id,
        }),
      (data) => {
        setToastConfig({
          severity: "success",
          summary: "Eliminado",
          detail: "Parte interesada eliminada correctamente.",
        });
        setShowToast(true);
        getTableData();
      }
    );
    setOpenDeleteVerification(false);
    closeModal();
    setSelectedRow(null);
  };

  const handleOpenVerification = async () => {
    const isValid = await trigger();
    if (isValid) {
      const dataToSend = getValues();
      setSelectedEvent(dataToSend);
      setOpenVerification(true);
    }
  };

  const handleCloseVerification = () => setOpenVerification(false);

  const handleOpenDeleteVerification = (row) => {
    setSelectedRow(row);
    setOpenDeleteVerification(true);
  };

  const handleCloseDeleteVerification = () => {
    setOpenDeleteVerification(false);
    setSelectedRow(null);
  };

  useEffect(() => {
    getTableData();
  }, []);

  return (
    <div>
      <BreadCrumbComp
        personalized={`Partes Interesadas externas - ${rowData?.name}`}
      />
      <h1 className="gm-header-title-grey mb-25">
        PARTES INTERESADAS EXTERNAS - {rowData?.name}
      </h1>
      <SimpleTable
        data={tableData}
        columns={columnsInterestedPartiesExternalBusinessCustomer}
        globalFilterFields={["name"]}
        headerButton={
          canEditModuleStakeholders ? (
            <LaddaButton
              variant="contained"
              width={"270px"}
              height={"40px"}
              onClick={openModal}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <img
                  className="btn-icon"
                  src={icons.addPlus}
                  alt="icon"
                  style={{ marginRight: "10px" }}
                />
                <p className="button_text">{`Crear necesidad`}</p>
              </div>
            </LaddaButton>
          ) : (
            <></>
          )
        }
        tableStyle={{ minWidth: "870px" }}
        buttons={[
          {
            type: "icon",
            icon: icons.eye,
            onClick: (row) => openModal(row, true),
          },
          ...(canEditModuleStakeholders
            ? [
                {
                  type: "icon",
                  icon: icons.editBlue,
                  onClick: (row) => openModal(row),
                },
                {
                  type: "icon",
                  icon: icons.redTrash,
                  onClick: handleOpenDeleteVerification,
                },
              ]
            : []),
        ]}
        isPaginated={true}
      />
      <ModalComponent
        open={open}
        handleClose={closeModal}
        title={`${isEditMode ? "Editar" : "Crear"} parte interesada para ${
          rowData?.name
        }`}
        actions={<div className="HContainer j-center w-complete" />}
      >
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <FormTextField
                name="requirement"
                control={control}
                isTextArea={true}
                maxLength={1000}
                label={
                  <>
                    Necesidad: <span className="red-text">*</span>
                  </>
                }
                error={errors.requirement}
                margin={"0"}
                disabled={viewOnly || !canEditModuleStakeholders}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <FormTextField
                name="expectation"
                control={control}
                isTextArea={true}
                maxLength={1000}
                label={
                  <>
                    Expectativa: <span className="red-text">*</span>
                  </>
                }
                error={errors.expectation}
                margin={"0"}
                disabled={viewOnly || !canEditModuleStakeholders}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <FormTextField
                name="accountability"
                control={control}
                maxLength={500}
                label={
                  <>
                    Rendición de cuentas: <span className="red-text">*</span>
                  </>
                }
                error={errors.accountability}
                margin={"0"}
                disabled={viewOnly || !canEditModuleStakeholders}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <FormTextField
                name="monitoring_sources"
                control={control}
                maxLength={500}
                label={
                  <>
                    Monitoreo de fuentes: <span className="red-text">*</span>
                  </>
                }
                error={errors.monitoring_sources}
                margin={"0"}
                disabled={viewOnly || !canEditModuleStakeholders}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <p className="create-label ml-0" style={{ color: "#6a6ad8" }}>
                Requisito legal: <span className="red-text">*</span>
              </p>
              <SimpleSelect
                name="legal_requirement"
                control={control}
                options={YesNoOptionsSelect}
                label=""
                error={errors.legal_requirement}
                margin={"0"}
                disabled={viewOnly || !canEditModuleStakeholders}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <p className="create-label ml-0" style={{ color: "#6a6ad8" }}>
                Requisito de la Organización:{" "}
                <span className="red-text">*</span>
              </p>
              <SimpleSelect
                name="organization_requirement"
                control={control}
                options={YesNoOptionsSelect}
                label=""
                error={errors.organization_requirement}
                margin={"0"}
                disabled={viewOnly || !canEditModuleStakeholders}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <p className="create-label ml-0" style={{ color: "#6a6ad8" }}>
                Requisito del cliente: <span className="red-text">*</span>
              </p>
              <SimpleSelect
                name="customer_requirement"
                control={control}
                options={YesNoOptionsSelect}
                label=""
                error={errors.customer_requirement}
                margin={"0"}
                disabled={viewOnly || !canEditModuleStakeholders}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <FormTextField
                name="frequency"
                control={control}
                maxLength={1000}
                label={
                  <>
                    Frecuencia: <span className="red-text">*</span>
                  </>
                }
                error={errors.frequency}
                margin={"0"}
                disabled={viewOnly || !canEditModuleStakeholders}
              />
            </Grid>
          </Grid>
          {!viewOnly && canEditModuleStakeholders && (
            <div className="HContainer j-center white-space">
              <LaddaButton
                width={"170px"}
                fontSize={"12px"}
                height={"40px"}
                variant="outlined"
                onClick={handleOpenVerification}
              >
                <p>{isEditMode ? "Editar" : "Crear"}</p>
              </LaddaButton>

              {!viewOnly && canEditModuleStakeholders && (
                <VerificationModal
                  open={openVerification}
                  handleClose={handleCloseVerification}
                  alertText={
                    <>
                      <b>
                        {isEditMode
                          ? "¿Está seguro de guardar los cambios?"
                          : "¿Está seguro de crear la necesidad?"}
                      </b>
                    </>
                  }
                  onConfirm={handleSaveBannerConfirmed}
                />
              )}
            </div>
          )}
        </form>
      </ModalComponent>
      <VerificationModal
        open={openDeleteVerification}
        handleClose={handleCloseDeleteVerification}
        alertText={
          <>
            <b>¿Estás seguro que deseas eliminar esta parte interesada?</b>
          </>
        }
        onConfirm={deleteInterestedPartiesExternalDetail}
      />
      <ToastNotification
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
        showToast={showToast}
        setShowToast={setShowToast}
        position="top-right"
      />
    </div>
  );
};

export default DetailBusinessCustomers;
